import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useI18n } from "../i18n";

export default function Catalog() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("-created_at");

  const category = params.get("category") || "";
  const type = params.get("type") || "";
  const flash = params.get("flash");
  const isNew = params.get("new");
  const top = params.get("top");
  const search = params.get("q") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (category) q.set("category", category);
    if (type) q.set("type", type);
    if (flash) q.set("flash_deal", "true");
    if (isNew) q.set("new_release", "true");
    if (top) q.set("top_seller", "true");
    if (search) q.set("search", search);
    q.set("sort", sort);
    q.set("limit", "60");
    api.get(`/products?${q.toString()}`).then((r) => {
      setProducts(r.data.items);
      setLoading(false);
    });
  }, [category, type, flash, isNew, top, search, sort]);

  return (
    <div className="min-h-screen pb-20" data-testid="catalog-page">
      <div className="gf-container py-8">
        <nav className="text-sm text-slate-500 mb-4 flex items-center gap-1.5">
          <Link to="/" className="hover:text-[#A855F7]">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-slate-900 font-medium">{category || type || "Catalog"}</span>
        </nav>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900">
              {category || (type === "gift_card" ? "Gift Cards" : type === "subscription" ? "Subscriptions" : type === "premium_account" ? "Premium Accounts" : flash ? "Flash Deals" : isNew ? "New Releases" : top ? "Top Sellers" : search ? `"${search}"` : "All Products")}
            </h1>
            <p className="text-slate-500 mt-1">{products.length} products</p>
          </div>
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-slate-500" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#A855F7]"
              data-testid="sort-select"
            >
              <option value="-created_at">Newest</option>
              <option value="price">Price: Low to High</option>
              <option value="-price">Price: High to Low</option>
              <option value="-rating">Rating</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-2xl bg-white border border-slate-200 p-5">
              <h3 className="font-display font-semibold mb-4 text-slate-900 text-sm uppercase tracking-wider">Categories</h3>
              <div className="space-y-1">
                <button
                  onClick={() => setParams({})}
                  className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    !category ? "bg-[#A855F7] text-white font-semibold" : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  All
                </button>
                {categories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setParams({ category: c.name })}
                    className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      category === c.name ? "bg-[#A855F7] text-white font-semibold" : "text-slate-700 hover:bg-slate-100"
                    }`}
                    data-testid={`filter-${c.slug}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <div>
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="aspect-[3/4] rounded-2xl shimmer" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl bg-white border border-slate-200 p-16 text-center">
                <p className="text-slate-500">No products match your filters.</p>
              </motion.div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
